<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useInsightAuthoring } from '../../../composables/useInsightAuthoring'
import type { InsightDraftWarning } from '../../../models/insightAuthoring.types'

import AuthoringComposer from './AuthoringComposer.vue'
import AuthoringDraftDiff from './AuthoringDraftDiff.vue'
import AuthoringTurnList from './AuthoringTurnList.vue'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { useConfirm, useToast } from '@/shared/ui/feedback'
import { Modal } from '@/shared/ui/modal'
import { PageHeader } from '@/shared/ui/page-header'

const props = defineProps<{
  sessionId?: string
  newRouteName: string
  sessionRouteName: string
  configEditRouteName: string
}>()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const confirm = useConfirm()
const toast = useToast()
const initialSeed = typeof route.query.seed === 'string' ? route.query.seed : ''
const message = ref(initialSeed)
const modalOpen = ref(false)
const authoring = useInsightAuthoring({
  sessionId: props.sessionId,
  onSessionId: (sessionId) => {
    void router.replace({ name: props.sessionRouteName, params: { sessionId } })
  },
  onActivated: (configId) => {
    void router.push({ name: props.configEditRouteName, params: { configId } })
  },
})

const inputError = computed(() =>
  authoring.error.value?.target === 'input' ? t(authoring.error.value.messageKey) : undefined,
)
const previewError = computed(() =>
  authoring.error.value?.target === 'preview' ? authoring.error.value : null,
)
const workspaceError = computed(() =>
  authoring.error.value?.target === 'workspace' ? authoring.error.value : null,
)
const fullpageError = computed(() =>
  authoring.error.value?.kind === 'fullpage' ? authoring.error.value : null,
)

watch(
  () => authoring.error.value,
  (error) => {
    if (!error) return
    if (error.kind === 'toast') {
      toast.show({ title: t(error.titleKey), message: t(error.messageKey), tone: error.tone })
    }
    if (error.kind === 'modal') modalOpen.value = true
  },
)

async function send(): Promise<void> {
  const outcome = await authoring.send(message.value)
  if (outcome.kind === 'received') message.value = ''
}

async function save(): Promise<void> {
  if (await authoring.save()) toast.success(t('insights.sysadmin.authoring.notice.saved'))
}

async function dryRun(): Promise<void> {
  const response = await authoring.dryRun()
  if (response?.status === 'preview_ready') {
    toast.success(t('insights.sysadmin.authoring.notice.previewReady'))
  }
}

async function startOver(): Promise<void> {
  if (authoring.turns.value.length > 0) {
    const accepted = await confirm({
      title: t('insights.sysadmin.authoring.startOver.title'),
      message: t('insights.sysadmin.authoring.startOver.message'),
      confirmLabel: t('insights.sysadmin.authoring.startOver.confirm'),
      cancelLabel: t('insights.sysadmin.config.cancel'),
      danger: true,
    })
    if (!accepted) return
  }
  authoring.startOver()
  message.value = ''
  await router.replace({ name: props.newRouteName })
}

function suggestFix(warnings: InsightDraftWarning[]): void {
  message.value = t('insights.sysadmin.authoring.turns.suggestFixPrompt', {
    warnings: warnings.map((warning) => warning.msg).join('\n'),
  })
}

function closeErrorModal(): void {
  modalOpen.value = false
  authoring.clearError()
}
</script>

<template>
  <main class="authoring-wizard grid gap-lg" data-testid="insight-authoring-wizard">
    <PageHeader
      :title="t('insights.sysadmin.authoring.wizardTitle')"
      :subtitle="t('insights.sysadmin.authoring.wizardSubtitle')"
    >
      <template #actions>
        <Button variant="ghost" @click="startOver">
          {{ t('insights.sysadmin.authoring.startOver.action') }}
        </Button>
      </template>
    </PageHeader>

    <Alert v-if="!authoring.canAuthor.value" tone="warning">
      {{ t('insights.sysadmin.config.forbidden') }}
    </Alert>
    <Alert v-else-if="fullpageError" tone="danger" :title="t(fullpageError.titleKey)">
      {{ t(fullpageError.messageKey) }}
    </Alert>
    <template v-else>
      <Alert v-if="!authoring.hasWorkspace.value" tone="warning">
        {{ t('insights.sysadmin.authoring.workspaceRequired') }}
      </Alert>
      <Alert v-if="workspaceError" tone="danger" :title="t(workspaceError.titleKey)">
        {{ t(workspaceError.messageKey) }}
      </Alert>
      <div
        class="authoring-wizard__layout grid grid-cols-[minmax(18rem,_1fr)_minmax(20rem,_1fr)] gap-lg max-[900px]:grid-cols-[1fr]"
      >
        <section class="authoring-wizard__conversation grid content-start gap-md">
          <AuthoringTurnList :turns="authoring.turns.value" @suggest-fix="suggestFix" />
          <AuthoringComposer
            v-model="message"
            :disabled="!authoring.canSend.value"
            :sending="authoring.requestState.value === 'sending'"
            :remaining-turns="authoring.remainingTurns.value"
            :error-message="inputError"
            @send="send"
          />
        </section>
        <aside class="authoring-wizard__preview grid content-start gap-md">
          <Alert v-if="previewError" tone="warning" :title="t(previewError.titleKey)">
            {{ t(previewError.messageKey) }}
          </Alert>
          <AuthoringDraftDiff :draft="authoring.lastDraft.value" :diff="authoring.lastDiff.value" />
          <div class="authoring-wizard__actions flex flex-wrap gap-sm">
            <Button
              :disabled="!authoring.canSave.value"
              :loading="authoring.requestState.value === 'saving'"
              @click="save"
            >
              {{ t('insights.sysadmin.authoring.actions.save') }}
            </Button>
            <Button
              variant="outline"
              :disabled="!authoring.canDryRun.value"
              :loading="authoring.requestState.value === 'dry_running'"
              @click="dryRun"
            >
              {{ t('insights.sysadmin.authoring.actions.dryRun') }}
            </Button>
            <Button
              :disabled="!authoring.canActivate.value"
              :loading="authoring.requestState.value === 'activating'"
              @click="authoring.activate('active')"
            >
              {{ t('insights.sysadmin.authoring.actions.activate') }}
            </Button>
          </div>
          <p class="authoring-wizard__activation-note m-0 text-muted text-[0.8125rem]">
            {{ t('insights.sysadmin.authoring.activationWindow') }}
          </p>
        </aside>
      </div>
    </template>

    <Modal
      v-model="modalOpen"
      :title="authoring.error.value ? t(authoring.error.value.titleKey) : ''"
      size="sm"
      @update:model-value="(open) => !open && closeErrorModal()"
    >
      <template #body>
        <p v-if="authoring.error.value">{{ t(authoring.error.value.messageKey) }}</p>
      </template>
      <template #footer>
        <Button @click="closeErrorModal">{{ t('common.close') }}</Button>
      </template>
    </Modal>
  </main>
</template>
